import { useState, useCallback, useRef, useEffect } from 'react'
import type { PlatinumTableData } from '../components/platinum-table-old/types'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'

interface ChatMessage {
  id: string
  role: 'system' | 'user' | 'assistant'
  content: string
  timestamp?: Date
  hasImage?: boolean
}

interface UseVisualizationChatProps {
  currentSlideData: PlatinumTableData | null
  currentSlideTitle: string | null
  currentSlideDescription: string | null
  jobId?: string | null
  customerId?: string | null
}

interface UseVisualizationChatReturn {
  // State
  isChatDrawerOpen: boolean
  chatMessages: ChatMessage[]
  chatInput: string
  isChatLoading: boolean
  chatError: string | null
  configError: string | null

  // Actions
  toggleChatDrawer: () => void
  setChatInput: (input: string) => void
  sendChatMessage: (model: string, input: string) => Promise<void>
  clearChatHistory: () => void
  generateInitialInsight: () => Promise<void>
}

// Helper to generate unique IDs
const generateId = () => Math.random().toString(36).substring(2, 15)

export const useVisualizationChat = ({
  currentSlideData,
  currentSlideTitle,
  currentSlideDescription,
  jobId,
  customerId
}: UseVisualizationChatProps): UseVisualizationChatReturn => {
  const [isChatDrawerOpen, setIsChatDrawerOpen] = useState(false)
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [chatInput, setChatInput] = useState('')
  const [isChatLoading, setIsChatLoading] = useState(false)
  const [chatError, setChatError] = useState<string | null>(null)
  const [configError, setConfigError] = useState<string | null>(null)

  // Track the current slide to detect changes
  const currentSlideRef = useRef<string | null>(null)
  const currentJobId = useRef<string | null>(null)
  const currentCustomerId = useRef<string | null>(null)

  // Clear chat messages when slide, job, or customer changes
  useEffect(() => {
    const jobChanged = jobId !== currentJobId.current
    const customerChanged = customerId !== currentCustomerId.current
    const slideChanged = currentSlideTitle && currentSlideTitle !== currentSlideRef.current

    if (jobChanged || customerChanged || slideChanged) {
      console.log('Context changed, clearing chat history', {
        jobChanged,
        customerChanged,
        slideChanged,
        currentSlide: currentSlideTitle
      })
      setChatMessages([])
      currentSlideRef.current = currentSlideTitle || null
      currentJobId.current = jobId || null
      currentCustomerId.current = customerId || null
    }
  }, [currentSlideTitle, jobId, customerId])

  // Generate context from current visualization
  const generateVisualizationContext = useCallback(() => {
    if (!currentSlideTitle) {
      return "No visualization is currently selected."
    }

    let context = `Current visualization: ${currentSlideTitle}\n`

    if (currentSlideDescription) {
      context += `Description: ${currentSlideDescription}\n`
    }

    if (currentSlideData) {
      const cells = currentSlideData.Cells || currentSlideData.cells
      const rows = cells?.Rows || cells?.rows
      const rowCount = rows?.length || 0
      const topVecs = currentSlideData.Top?.Vecs || currentSlideData.top?.vecs
      const columnCount = topVecs?.length || 0

      context += `\nUnderlying data has ${rowCount} rows and ${columnCount} columns.`

      // Get column labels
      if (topVecs && topVecs.length > 0) {
        const columnLabels = topVecs
          .filter((vec: any) => vec.Type !== 'Spacer')
          .map((vec: any) => vec.Label || vec.label || 'Unknown column')
          .join(', ')
        context += `\nColumns: ${columnLabels}`
      }
    }

    return context
  }, [currentSlideTitle, currentSlideDescription, currentSlideData])

  // Toggle chat drawer
  const toggleChatDrawer = useCallback(() => {
    setIsChatDrawerOpen(prev => !prev)
  }, [])

  // Clear chat history
  const clearChatHistory = useCallback(() => {
    setChatMessages([])
  }, [])

  // Generate initial insight for the current visualization
  const generateInitialInsight = useCallback(async () => {
    if (!currentSlideData || !currentSlideTitle) return

    try {
      setIsChatLoading(true)
      setChatError(null)

      // Create a simplified data summary for the initial insight
      const context = generateVisualizationContext()

      const systemMessage: ChatMessage = {
        id: generateId(),
        role: 'system',
        content: `You are Platinum Insights, analyzing data visualizations for Sky's market research platform.

Current context:
${context}

Provide a brief, insightful analysis of what this visualization shows, focusing on key trends and actionable insights.`
      }

      const assistantMessage: ChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: `I'm ready to analyze the "${currentSlideTitle}" visualization. This chart ${currentSlideDescription || 'displays important data insights'}.

Based on the underlying data structure, I can help you:
- Identify key trends and patterns
- Compare different segments or time periods
- Understand the significance of the data
- Generate actionable recommendations

What specific aspect of this visualization would you like to explore?`
      }

      setChatMessages([systemMessage, assistantMessage])
    } catch (error) {
      console.error('Error generating initial insight:', error)
      setChatError('Failed to generate initial insight')
    } finally {
      setIsChatLoading(false)
    }
  }, [currentSlideData, currentSlideTitle, currentSlideDescription, generateVisualizationContext])

  // Send a chat message with optional chart image
  const sendChatMessage = useCallback(async (model: string, inputText: string) => {
    const trimmed = inputText.trim()
    if (!trimmed || isChatLoading) return

    try {
      setIsChatLoading(true)
      setChatError(null)

      // Add user message
      const userMessage: ChatMessage = {
        id: generateId(),
        role: 'user',
        content: trimmed,
        timestamp: new Date()
      }

      setChatMessages(prev => [...prev, userMessage])

      // Prepare context with visualization information
      const context = generateVisualizationContext()

      // Build messages array with context
      const messages = [
        {
          role: 'system' as const,
          content: `You are Platinum Insights, analyzing data visualizations for Sky's market research platform.

${context}

Provide clear, actionable insights based on the visualization and data.`
        },
        ...chatMessages.map(msg => ({
          role: msg.role,
          content: msg.content
        })),
        {
          role: 'user' as const,
          content: trimmed
        }
      ]

      // Use carbon client to send message
      const response = await axios.post<ChatMessage[]>(`${API_BASE_URL}/chat/table/analysis/chat`, {
        messages,
        model,
      })

      if (response && response.data) {
        setChatMessages(response.data)
      } else {
        throw new Error('Invalid response from AI service')
      }
    } catch (error) {
      console.error('Error sending message:', error)
      setChatError('Failed to send message. Please try again.')
    } finally {
      setIsChatLoading(false)
    }
  }, [chatMessages, isChatLoading, generateVisualizationContext])

  return {
    isChatDrawerOpen,
    chatMessages,
    chatInput,
    isChatLoading,
    chatError,
    configError,
    toggleChatDrawer,
    setChatInput,
    sendChatMessage,
    clearChatHistory,
    generateInitialInsight
  }
}

export default useVisualizationChat